<div class="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
  <!-- Breadcrumbs -->
  <div class="mb-6 flex flex-wrap items-center gap-1.5">
    <span class="text-sm font-medium leading-5 text-neutral-400">Number and algebra</span>
    <dojo-icon [icon]="'angle-right'" [size]="10" class="text-neutral-400" />
    <span class="text-sm font-medium leading-5 text-neutral-400">SL 1.3 – Quadratic functions</span>
  </div>

  <!-- Main Content -->
  <div class="pb-8">
    <!-- Title -->
    <h1 class="mb-8 text-2xl font-semibold leading-8 text-neutral-900">SL 1.3 – Quadratic functions</h1>

    <!-- Definition Section -->
    <div class="mb-8 flex flex-col gap-3">
      <!-- Section Header -->
      <div class="flex items-center gap-3">
        <img src="/assets/playground/quadratic-functions/definition.svg" alt="Definition" class="h-6 w-6" />
        <h2 class="text-xl font-semibold leading-7 text-neutral-900">Definition</h2>
      </div>

      <!-- Content -->
      <div class="flex flex-col gap-2">
        <p class="text-base leading-6 text-neutral-900">
          A quadratic function is a function that can be written in the form:
        </p>

        <!-- Formula -->
        <div class="flex justify-center py-4">
          <dojo-markdown-viewer [content]="'$$f(x) = ax^2 + bx + c$$'" />
        </div>

        <!-- Explanation -->
        <div class="flex flex-col gap-2">
          <p class="flex flex-wrap items-baseline gap-1 text-base leading-6 text-neutral-900">
            <span>where</span>
            <dojo-markdown-viewer class="inline-block" [content]="'$a, b, c$'" />
            <span>are constants and</span>
            <dojo-markdown-viewer class="inline-block" [content]="'$a \\neq 0$'" />
            <span>.</span>
          </p>
          <p class="flex flex-wrap items-baseline gap-1 text-base leading-6 text-neutral-900">
            <span>In simpler terms it's a function where the highest power of</span>
            <dojo-markdown-viewer class="inline-block" [content]="'$x$'" />
            <span>is 2.</span>
          </p>
        </div>
      </div>
    </div>

    <!-- Divider -->
    <div class="my-12 h-px w-full bg-neutral-200"></div>

    <!-- Graph Section -->
    <div class="flex flex-col gap-3">
      <!-- Section Header -->
      <div class="flex items-center gap-3">
        <img src="/assets/playground/quadratic-functions/graph.svg" alt="Graph" class="h-6 w-6" />
        <h2 class="text-xl font-semibold leading-7 text-neutral-900">Graph</h2>
      </div>

      <!-- Curve Shape Subsection -->
      <div class="mt-3">
        <h3 class="mb-4 text-lg font-semibold leading-7 text-neutral-900">Curve shape</h3>
        <p class="mb-6 text-base leading-6 text-neutral-900">
          The graph of a quadratic function is a parabola, a smooth U-shaped curve that can open upward or downward
          depending on the value of a.
        </p>

        <!-- Parabola Cards -->
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <!-- Concave Up Card -->
          <div class="flex flex-col gap-3">
            <div class="bg-neutral-0 overflow-hidden">
              <img
                src="/assets/playground/quadratic-functions/images/concave-up.svg"
                alt="Concave up parabola"
                class="block h-auto w-full"
              />
            </div>
            <div class="flex items-start gap-1.5 text-base leading-6 text-neutral-900">
              <div class="flex h-full w-[120px] items-center justify-center">
                <dojo-markdown-viewer
                  class="inline-block [&_markdown]:my-0 [&_markdown_p]:my-0"
                  [content]="'$a > 0$'"
                  [applyProseClass]="false"
                />
              </div>
              <span
                >The parabola is <span class="font-semibold">concave up</span> and has a
                <span class="font-semibold">minimum</span> point</span
              >
            </div>
          </div>

          <!-- Concave Down Card -->
          <div class="flex flex-col gap-3">
            <div class="bg-neutral-0 overflow-hidden">
              <img
                src="/assets/playground/quadratic-functions/images/concave-down.svg"
                alt="Concave down parabola"
                class="block h-auto w-full"
              />
            </div>
            <div class="flex items-start gap-1.5 text-base leading-6 text-neutral-900">
              <div class="flex h-full w-[120px] items-center justify-center">
                <dojo-markdown-viewer
                  class="inline-block [&_markdown]:my-0 [&_markdown_p]:my-0"
                  [content]="'$a < 0$'"
                  [applyProseClass]="false"
                />
              </div>
              <span
                >The parabola is <span class="font-semibold">concave down</span> and has a
                <span class="font-semibold">maximum</span> point</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Key Features Subsection -->
      <div class="mt-8">
        <h3 class="mb-4 text-lg font-semibold leading-7 text-neutral-900">Key Features</h3>
        <p class="mb-6 text-base leading-6 text-neutral-900">
          A quadratic function has several geometric characteristics that define its graph. The coefficients
          <dojo-markdown-viewer class="inline-block" [content]="'$a, b,$'" /> and
          <dojo-markdown-viewer class="inline-block" [content]="'$c$'" /> determine its position and shape on the
          coordinate plane
        </p>

        <!-- Interactive Graph Placeholder -->
        <div class="mb-6 flex min-h-[500px] items-center justify-center bg-neutral-50">
          <p class="text-base text-neutral-400">Interactive Graph Placeholder</p>
        </div>

        <!-- Features List -->
        <ul class="ml-5 flex list-disc flex-col gap-2">
          <!-- Y-intercept -->
          <li class="text-base leading-6 text-neutral-900">
            <span class="font-semibold">Y-intercept:</span>
            <span> Point </span>
            <dojo-markdown-viewer
              class="inline-block [&_markdown]:my-0 [&_markdown_p]:my-0"
              [content]="'$(0, c)$'"
              [applyProseClass]="false"
            />
          </li>

          <!-- Roots -->
          <li class="text-base leading-6 text-neutral-900">
            <span class="font-semibold">Roots:</span>
            <span> Solutions </span>
            <dojo-markdown-viewer
              class="inline-block [&_markdown]:my-0 [&_markdown_p]:my-0"
              [content]="'$ax^2 + bx + c = 0$'"
              [applyProseClass]="false"
            />
            <span> with 0, 1 or 2 possible roots</span>
          </li>

          <!-- Vertex -->
          <li class="text-base leading-6 text-neutral-900">
            <span class="font-semibold">Vertex:</span>
            <span> Highest or lowest point on its graph at </span>
            <dojo-markdown-viewer
              class="inline-block [&_markdown]:my-0 [&_markdown_p]:my-0"
              [content]="'$\\left(-\\frac{b}{2a}, f\\left(-\\frac{b}{2a}\\right)\\right)$'"
              [applyProseClass]="false"
            />
          </li>

          <!-- Axis of symmetry -->
          <li class="text-base leading-6 text-neutral-900">
            <span class="font-semibold">Axis of symmetry:</span>
            <span> Vertical line that divides the parabola halves at </span>
            <dojo-markdown-viewer
              class="inline-block [&_markdown]:my-0 [&_markdown_p]:my-0"
              [content]="'$x = -\\frac{b}{2a}$'"
              [applyProseClass]="false"
            />
          </li>
        </ul>
      </div>
    </div>

    <!-- Divider -->
    <div class="my-12 h-px w-full bg-neutral-200"></div>

    <!-- Methods Section -->
    <div class="flex flex-col gap-3">
      <!-- Section Header -->
      <div class="flex items-center gap-3">
        <img src="/assets/playground/quadratic-functions/function.svg" alt="Methods" class="h-6 w-6" />
        <h2 class="text-xl font-semibold leading-7 text-neutral-900">Methods</h2>
      </div>

      <!-- Quadratic Formula Subsection -->
      <div class="mt-3">
        <h3 class="mb-4 text-lg font-semibold leading-7 text-neutral-900">Quadratic Formula</h3>
        <p class="mb-6 text-base leading-6 text-neutral-900">
          The quadratic formula is used to find the roots of a quadratic function:
        </p>

        <!-- Formula -->
        <div class="mb-6 flex justify-center py-4">
          <dojo-markdown-viewer
            [content]="
              '$$ax^2 + bx + c = 0 \\quad \\Rightarrow \\quad x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a} = \\frac{-b \\pm \\sqrt{\\Delta}}{2a}, \\quad a \\neq 0$$'
            "
          />
        </div>

        <!-- Discriminant Text -->
        <p class="mb-6 text-base leading-6 text-neutral-900">
          The discriminant
          <dojo-markdown-viewer
            class="inline-block [&_markdown]:my-0 [&_markdown_p]:my-0"
            [content]="'$\\Delta = b^2 - 4ac$'"
            [applyProseClass]="false"
          />
          determines the nature of the roots:
        </p>

        <!-- Discriminant Cards -->
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <!-- Two Roots -->
          <div class="flex flex-col items-center gap-3 text-center">
            <div class="bg-neutral-0 w-full overflow-hidden">
              <img
                src="/assets/playground/quadratic-functions/images/two-roots.svg"
                alt="Two roots"
                class="block h-auto w-full"
              />
            </div>
            <div class="flex items-start gap-3">
              <div class="flex h-full items-center justify-center">
                <dojo-markdown-viewer
                  class="inline-block [&_markdown]:my-0 [&_markdown_p]:my-0"
                  [content]="'$\\Delta > 0$'"
                  [applyProseClass]="false"
                />
              </div>
              <div class="flex flex-col text-left text-base leading-6 text-neutral-900">
                <span><span class="font-semibold">two roots</span> (crosses</span>
                <span>x-axis two times)</span>
              </div>
            </div>
          </div>

          <!-- One Root -->
          <div class="flex flex-col items-center gap-3 text-center">
            <div class="bg-neutral-0 w-full overflow-hidden">
              <img
                src="/assets/playground/quadratic-functions/images/one-root.svg"
                alt="One root"
                class="block h-auto w-full"
              />
            </div>
            <div class="flex items-start gap-3">
              <div class="flex h-full items-center justify-center">
                <dojo-markdown-viewer
                  class="inline-block [&_markdown]:my-0 [&_markdown_p]:my-0"
                  [content]="'$\\Delta = 0$'"
                  [applyProseClass]="false"
                />
              </div>
              <div class="flex flex-col text-left text-base leading-6 text-neutral-900">
                <span><span class="font-semibold">one root</span> (vertex</span>
                <span>touches x-axis)</span>
              </div>
            </div>
          </div>

          <!-- No Roots -->
          <div class="flex flex-col items-center gap-3 text-center">
            <div class="bg-neutral-0 w-full overflow-hidden">
              <img
                src="/assets/playground/quadratic-functions/images/no-roots.svg"
                alt="No roots"
                class="block h-auto w-full"
              />
            </div>
            <div class="flex items-start gap-3">
              <div class="flex h-full items-center justify-center">
                <dojo-markdown-viewer
                  class="inline-block [&_markdown]:my-0 [&_markdown_p]:my-0"
                  [content]="'$\\Delta < 0$'"
                  [applyProseClass]="false"
                />
              </div>
              <div class="flex flex-col text-left text-base leading-6 text-neutral-900">
                <span><span class="font-semibold">no roots</span> (does</span>
                <span>not cross x-axis)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Divider -->
    <div class="my-12 h-px w-full bg-neutral-200"></div>

    <!-- Worked Examples Section -->
    <div class="flex flex-col gap-3">
      <!-- Section Header -->
      <div class="flex items-center gap-3">
        <img src="/assets/playground/quadratic-functions/worked-example.svg" alt="Worked Examples" class="h-6 w-6" />
        <h2 class="text-xl font-semibold leading-7 text-neutral-900">Worked Examples</h2>
      </div>

      <!-- Example -->
      <div class="mt-3 flex flex-col gap-6">
        <p class="text-base leading-6 text-neutral-900">Solve the given quadratic equation</p>

        <!-- Calculator -->
        <exam-basic-quadratic-calculator />
      </div>
    </div>
  </div>
</div>
